// Top-level AI decision pipeline. See docs/05-combat-sim §5.6 for the
// canonical 7-stage order:
//   1. Targeting (head)
//   2. Threat Policy (chest) -> applied during the AI prep phase.
//   3. Position Policy (amulet) -> anchor + offset
//   4. Movement (boots) -> desired position
//   5. Tempo (hands) -> skill firing
//   6. DamageReact (rings) -> threshold-triggered overrides
//   7. Ultimate (job) -> ignores Movement / Tempo when ready
//
// Phase 5.x-B1/B2 wires Stage 7 ultimate priority. Phase 5.x-B3 wires
// DamageReact trigger evaluation and priority-based temporary overrides.
import { resolveActiveDamageReactOverrides } from './damage-react.js';
import { computeMovement } from './movement.js';
import { computeAnchor } from './position.js';
import { pickSkill } from './tempo.js';
import { pickTarget } from './targeting.js';
import { DEFAULT_RULES, extractRules, } from './types.js';
export function decideAction(self, state, rng, options = {}) {
    const rules = extractRules(self);
    const damageReactOverrides = resolveActiveDamageReactOverrides(state.damageReactOverrides?.get(self.id), state.tick);
    // Step 1: Targeting
    const targetingRule = rules.targeting ?? DEFAULT_RULES.targeting;
    const targetId = pickTarget(self, state, targetingRule);
    // Step 2: Threat Policy is applied during AI prep (modifies entity.threatMod);
    // surface the rule choice in the trace for replays.
    const threatPolicyRule = damageReactOverrides.threatPolicy ?? rules.threatPolicy ?? DEFAULT_RULES.threatPolicy;
    // Step 3: Position Policy → anchor.
    const positionRule = damageReactOverrides.positionPolicy ?? rules.positionPolicy ?? DEFAULT_RULES.positionPolicy;
    const anchor = computeAnchor(self, state, positionRule, targetId);
    // Step 4: Movement → desired position.
    const movementRule = damageReactOverrides.movement ?? rules.movement ?? DEFAULT_RULES.movement;
    const desiredPos = computeMovement(self, state, movementRule, anchor, targetId);
    // Step 5: Tempo → skill firing.
    const tempoRule = damageReactOverrides.tempo ?? rules.tempo ?? DEFAULT_RULES.tempo;
    const candidates = options.candidates ?? [];
    const tempoCastSkillId = pickSkill(self, state, tempoRule, candidates, targetId, rng);
    // Step 6: DamageReact - active overrides have already been evaluated by the
    // tick loop, then synthesized into the effective movement/tempo/position
    // rules above. Trace the active source rules for replay/debug evidence.
    const damageReactsActive = damageReactOverrides.activeRuleIds;
    // Step 7: Ultimate - ready ultimates ignore Tempo and fire first. The tick
    // loop still re-checks range / cooldown / activation before applying effects.
    const readyUltimate = candidates.find((c) => c.ready && c.tags.has('ultimate'));
    const castSkillId = readyUltimate?.id ?? tempoCastSkillId;
    const trace = {
        targeting: targetingRule,
        movement: movementRule,
        positionPolicy: positionRule,
        tempo: tempoRule,
        threatPolicy: threatPolicyRule,
        damageReactsActive,
    };
    return { targetId, desiredPos, castSkillId, trace };
}
//# sourceMappingURL=decide.js.map